import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { useDeviceDetect } from '../../utils/use-device-detect'
import { useGetLocationsQuery } from '../../store/api/locationsApi'
import { useCheckoutMutation } from '../../store/api/ordersApi'
import { useSpinRouletteMutation } from '../../store/api/rouletteApi'
import { Store } from '../../utils/toast'
import L from 'leaflet'
import axios from 'axios'
import './CheckoutPage.scss'
import UserInfo from './components/UserInfo'
import LocationSelector from './components/LocationSelector'
import MapSelector from './components/MapSelector'
import { CustomInput } from '../../components/custom-input/CustomInput'
import PaymentSelector from './components/PaymentSelector'
import DeliveryScheduler from './components/DeliveryScheduler'
import OrderSummary, {
  OrderButton,
  orderTotals,
} from './components/OrderSummary'
import RouletteWheel from './components/RouletteWheel'
import SuccessModal from './components/SuccessModal/SuccessModal'

import { IProduct } from '../../types/products'
import { ILocation } from '../../types/locations'
import { IGeneralInfo } from '../../types/app'
import { getStorageKey } from '../../utils/appType'
import { haptic } from '../../utils/fx'

const c = 'checkout-page'

const CheckoutPage = () => {
  const generalInfo = JSON.parse(
    localStorage.getItem(getStorageKey('info')) || '{}',
  ) as IGeneralInfo
  const nearestValidTime = new Date()

  nearestValidTime.setMinutes(
    Math.ceil(nearestValidTime.getMinutes() / 15) * 15,
  )
  nearestValidTime.setSeconds(0)
  nearestValidTime.setHours(nearestValidTime.getHours() + 1)

  const { t, i18n } = useTranslation()
  const { isMobile, isDesktop } = useDeviceDetect()
  const { cart, setCart } = useOutletContext<{
    cart: IProduct[]
    setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
  }>()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [details, setDetails] = useState('')
  const [takeaway, setTakeaway] = useState(false)
  const [date, setDate] = useState<Date | null>(new Date())
  const [time, setTime] = useState<Date | null>(nearestValidTime)
  const [loading, setLoading] = useState(false)
  const { data: locations = [] } = useGetLocationsQuery()
  const [checkoutMutation] = useCheckoutMutation()
  const [spinRouletteMutation] = useSpinRouletteMutation()
  const [location, setLocation] = useState<ILocation | null>(null)
  const [position, setPosition] = useState<L.LatLng | null>(null)
  const [mapLink, setMapLink] = useState('')
  const [showMyLocation, setShowMyLocation] = useState(false)
  const [mapError, setMapError] = useState(false)
  const [showRoulette, setShowRoulette] = useState(false)
  const [prizeNumber, setPrizeNumber] = useState(0)
  const [prizeName, setPrizeName] = useState('')
  const [orderSuccess, setOrderSuccess] = useState(false)
  const [orderSubmitted, setOrderSubmitted] = useState(false)

  const orderPrice = cart.reduce(
    (total: number, item: IProduct) =>
      total + Number(item.price) * item.quantity!,
    0,
  )

  const [errorState, setErrorState] = useState({
    name: true,
    phone: true,
    location: true,
  })

  const [clicked, setClicked] = useState(false)
  const [open, setOpen] = useState(false)
  const [allHours, setAllHours] = useState(false)

  const [coordinates, setCoordinates] = useState<L.LatLngExpression>({
    lat: 48.35890104494752,
    lng: 27.09073581686573,
  } as L.LatLngExpression)

  const PAYMENT_METHODS = [
    { label: t('PAYMENT_CASH'), value: '1' },
    { label: t('PAYMENT_CARD_AT_COURIER'), value: '2' },
  ]

  const [payment, setPayment] = useState(PAYMENT_METHODS[0])

  const getTranslatedName = (item: ILocation) =>
    i18n.language === 'ru' && item?.label_ru ? item.label_ru : item.label

  const formatPhoneNumber = (phone: string) => {
    if (phone.startsWith('0') || phone.startsWith('+373')) {
      return phone.replace(/[()-\s]/g, '')
    }

    return `0${phone}`
  }

  const handleCheckout = () => {
    setClicked(true)

    if (!name || !phone || (!location && !takeaway)) {
      haptic([20, 40, 20])
      scrollTo({ top: 0, behavior: 'smooth' })

      return
    }

    if (!position) {
      haptic([20, 40, 20])
      setMapError(true)
      document
        .querySelector(`.${c}__map`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })

      return
    }

    if (
      generalInfo.isRouletteActive &&
      generalInfo.rouletteMinPrice < orderPrice
    ) {
      setShowRoulette(true)
      setLoading(false)
    } else {
      sendCheckoutRequest('')
      setLoading(true)
    }
  }

  const sendCheckoutRequest = (roulettePrize = '') => {
    const data = {
      name,
      phone: formatPhoneNumber(phone),
      location: location?.label || '',
      details,
      delivery_method: takeaway
        ? 'Pe Loc'
        : date?.toLocaleDateString('en-GB') ===
            new Date().toLocaleDateString('en-GB')
          ? 'Livrare'
          : 'Comanda Prealabila',
      payment: payment.label,
      time: time?.toLocaleTimeString(),
      date: date?.toLocaleDateString('en-GB'),
      delivery_cost: takeaway ? 0 : (location?.delivery_price ?? 0),
      order_price: orderPrice,
      total_price:
        orderPrice + (takeaway ? 0 : (location?.delivery_price ?? 0)),
      mapLink,
      roulette_prize: roulettePrize,
      cart,
    }

    checkoutMutation(data)
      .unwrap()
      .then(() => {
        sessionStorage.setItem('orderCompleted', 'true')

        if (roulettePrize) {
          localStorage.setItem(getStorageKey('roulettePrize'), roulettePrize)
        }

        let successMessage = t('ORDER_SUCCESS')

        if (
          generalInfo.isRouletteActive &&
          generalInfo.rouletteMinPrice < orderPrice &&
          roulettePrize
        ) {
          successMessage += ` ${t('YOU_WON')}: ${roulettePrize}!`
        }

        if (roulettePrize) {
          // Wheel is still spinning — store message, show modal once animation ends
          localStorage.setItem(getStorageKey('successMessage'), successMessage)
        } else {
          Store.addNotification({
            title: 'SUCCESS',
            message: successMessage,
            container: 'top-center',
            type: 'success',
            insert: 'top',
            animationIn: ['animate__animated', 'animate__fadeIn'],
            animationOut: ['animate__animated', 'animate__fadeOut'],
            dismiss: { duration: 5000, onScreen: true },
          })

          setOpen(true)
          setCart([])
          setLoading(false)
        }
      })
      .catch((err: any) => {
        setLoading(false)
        setShowRoulette(false)
        setOrderSubmitted(false)
        Store.addNotification({
          title: 'ERROR',
          message: `Este o eroare in sistem, va rugam sa ne contactati pe 069 388 892 sau 068 247 847\n${err?.data?.error ?? err?.message ?? ''}`,
          container: 'top-center',
          type: 'danger',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: { duration: 5000, onScreen: true },
        })
      })
  }

  const handleRouletteStop = (selectedOption: string) => {
    setPrizeName(selectedOption)
    setShowRoulette(false)

    const successMessage = localStorage.getItem(getStorageKey('successMessage'))

    if (successMessage) {
      Store.addNotification({
        title: 'SUCCESS',
        message: successMessage,
        container: 'top-center',
        type: 'success',
        insert: 'top',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: { duration: 5000, onScreen: true },
      })
      localStorage.removeItem(getStorageKey('successMessage'))
    }

    setOpen(true)
    setCart([])
    setLoading(false)
  }

  const handleRouletteSpin = async () => {
    setLoading(true)
    setOrderSubmitted(true)

    try {
      const response = await spinRouletteMutation().unwrap()
      const winningPrize = response.prize

      // Fire checkout in the background while wheel is spinning
      sendCheckoutRequest(winningPrize)

      return { prize: winningPrize }
    } catch (error) {
      setLoading(false)
      setOrderSubmitted(false)

      throw error
    }
  }

  useEffect(() => {
    scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    const pendingOrderSuccess = localStorage.getItem(
      getStorageKey('pendingOrderSuccess'),
    )
    const orderCompleted = sessionStorage.getItem('orderCompleted')

    if (pendingOrderSuccess === 'true' || orderCompleted === 'true') {
      setOpen(true)
      setCart([])
      setLoading(false)

      localStorage.removeItem(getStorageKey('pendingOrderSuccess'))

      const successMessage = localStorage.getItem(
        getStorageKey('successMessage'),
      )

      if (successMessage) {
        Store.addNotification({
          title: 'SUCCESS',
          message: successMessage,
          container: 'top-center',
          type: 'success',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: { duration: 5000, onScreen: true },
        })
        localStorage.removeItem(getStorageKey('successMessage'))
      }
    }
  }, [])

  useEffect(() => {
    if (clicked) {
      setErrorState({ name: !!name, phone: !!phone, location: !!location })
    }
  }, [name, phone, location, clicked])

  useEffect(() => {
    setAllHours(
      date?.toLocaleDateString('en-GB') !==
        new Date().toLocaleDateString('en-GB'),
    )
  }, [date])

  useEffect(() => {
    if (position) {
      setMapLink(`https://maps.google.com/?q=${position.lat},${position.lng}`)
    }
  }, [position])

  useEffect(() => {
    const fetchCoordinates = async (loc: string) => {
      if (!loc) {
        setCoordinates({ lat: 48.331216, lng: 26.87127 })

        return
      }

      try {
        const response = await axios.get(
          'https://nominatim.openstreetmap.org/search',
          {
            params: { q: `${loc}, Briceni, Moldova`, format: 'json' },
          },
        )

        if (response.data.length > 0) {
          const { lat, lon } = response.data[0]

          setCoordinates({ lat: parseFloat(lat), lng: parseFloat(lon) })
        }
      } catch (error) {
        console.error('Error fetching coordinates', error)
      }
    }

    fetchCoordinates(location?.label || '')
  }, [location])

  useEffect(() => {
    if (position) {
      setMapError(false)
    }
  }, [position])


  const contactDone = !!name && !!phone
  const addressDone = takeaway || (!!location && !!position)
  const { grandTotal } = orderTotals(cart, takeaway, location)

  const step = (index: number, title: string, done: boolean) => (
    <div className={`${c}__step-head`}>
      <span className={`${c}__step-num${done ? ' is-done' : ''}`}>
        {done ? <FontAwesomeIcon icon={faCheck} /> : index}
      </span>
      <h2>{title}</h2>
    </div>
  )

  return (
    <div className={c}>
      {isDesktop && <h1 className={`${c}__title`}>{t('DELIVERY')}</h1>}
      <div className={`${c}__grid`}>
        <div className={`${c}__steps`}>
          <section className={`${c}__step`} style={{ '--i': 0 } as React.CSSProperties}>
            {step(1, t('STEP_CONTACT'), contactDone)}
            <UserInfo
              name={name}
              setName={setName}
              phone={phone}
              setPhone={setPhone}
              errorState={errorState}
              t={t}
            />
          </section>

          <section className={`${c}__step`} style={{ '--i': 1 } as React.CSSProperties}>
            {step(2, t('STEP_ADDRESS'), addressDone)}
            <LocationSelector
              location={location}
              setLocation={setLocation}
              takeaway={takeaway}
              setTakeaway={setTakeaway}
              locations={locations}
              errorState={errorState}
              t={t}
              setPosition={setPosition}
              setCoordinates={setCoordinates}
              getTranslatedName={getTranslatedName}
            />
            <MapSelector
              position={position}
              setPosition={setPosition}
              coordinates={coordinates}
              showMyLocation={showMyLocation}
              setLocationFunction={() => setShowMyLocation(true)}
              mapError={mapError}
              t={t}
              loading={loading}
            />
            <CustomInput
              label={t('DETAILS')}
              fullWidth
              value={details}
              multiline
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              variant='filled'
            />
          </section>

          <section className={`${c}__step`} style={{ '--i': 2 } as React.CSSProperties}>
            {step(3, t('STEP_TIME'), !!date && !!time)}
            <DeliveryScheduler
              date={date}
              setDate={setDate}
              time={time}
              setTime={setTime}
              allHours={allHours}
              nearestValidTime={nearestValidTime}
              generalInfo={generalInfo}
              isMobile={isMobile}
              t={t}
            />
          </section>

          <section className={`${c}__step`} style={{ '--i': 3 } as React.CSSProperties}>
            {step(4, t('STEP_PAYMENT'), true)}
            <PaymentSelector
              payment={payment}
              setPayment={setPayment}
              paymentMethods={PAYMENT_METHODS}
            />
          </section>
        </div>

        <aside className={`${c}__aside`}>
          <OrderSummary
            cart={cart}
            takeaway={takeaway}
            location={location}
            loading={loading}
            handleCheckout={handleCheckout}
            t={t}
            showButton={isDesktop}
          />
        </aside>
      </div>

      {!isDesktop && (
        <div className={`${c}__bar`}>
          <div className={`${c}__bar-total`}>
            <small>{t('TOTAL')}</small>
            <strong key={grandTotal}>{grandTotal} lei</strong>
          </div>
          <OrderButton
            loading={loading}
            disabled={cart.length === 0}
            onClick={handleCheckout}
            label={t('ORDER')}
          />
        </div>
      )}
      {generalInfo.isRouletteActive &&
        generalInfo.rouletteMinPrice < orderPrice && (
          <RouletteWheel
            show={showRoulette}
            onStopSpinning={handleRouletteStop}
            onSpin={handleRouletteSpin}
            t={t}
            prizeNumber={prizeNumber}
            setPrizeNumber={setPrizeNumber}
          />
        )}
      <SuccessModal
        isOpen={open}
        setIsOpen={setOpen}
        generalInfo={generalInfo}
        orderPrice={orderPrice}
      />
    </div>
  )
}

export default CheckoutPage
